import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
    Heart,
    MessageCircle,
    Repeat2,
    Bookmark,
    BarChart2,
    Image as ImageIcon,
    X,
    Send,
    Sparkles,
    Flame,
    Clock,
    Trash2,
    AlertCircle,
    Loader2,
    Share2,
    Hash,
    Verified,
} from "lucide-react";
import UserAvatar from "../components/UserAvatar";

const MAX_CHARS = 500;

function timeAgo(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const s = Math.max(1, Math.round((Date.now() - d.getTime()) / 1000));
    if (s < 60) return `${s}s`;
    const m = Math.round(s / 60);
    if (m < 60) return `${m}m`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h`;
    return `${Math.round(h / 24)}d`;
}

function formatCount(n) {
    if (n < 1000) return String(n);
    if (n < 1_000_000) return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}K`.replace(".0", "");
    return `${(n / 1_000_000).toFixed(1)}M`.replace(".0", "");
}

// Linkify #hashtags and @mentions in post text
function renderRichText(text, onTagClick) {
    const parts = [];
    const regex = /(#[A-Za-z0-9_]{1,30}|@[A-Za-z0-9_]{2,24})/g;
    let last = 0;
    let match;
    let idx = 0;
    while ((match = regex.exec(text)) !== null) {
        if (match.index > last) parts.push(text.slice(last, match.index));
        const token = match[0];
        if (token.startsWith("#")) {
            const tag = token.slice(1);
            parts.push(
                <button
                    key={`t${idx++}`}
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onTagClick(tag); }}
                    className="text-vibrantblue hover:underline font-semibold"
                    data-testid={`kyc-hashtag-${tag.toLowerCase()}`}
                >{token}</button>
            );
        } else {
            const uname = token.slice(1);
            parts.push(
                <Link
                    key={`m${idx++}`}
                    to={`/u/${uname}`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-vibrantorange hover:underline font-semibold"
                >{token}</Link>
            );
        }
        last = match.index + token.length;
    }
    if (last < text.length) parts.push(text.slice(last));
    return parts;
}

function CharProgress({ length, max }) {
    const pct = Math.min(1, length / max);
    const R = 10;
    const C = 2 * Math.PI * R;
    const stroke = pct >= 1 ? "#EC4899" : pct >= 0.9 ? "#F97316" : "#0EA5E9";
    return (
        <div className="flex items-center gap-1.5">
            <svg width="28" height="28" viewBox="0 0 28 28">
                <circle cx="14" cy="14" r={R} fill="none" stroke="#e5e7eb" strokeWidth="3" />
                <circle
                    cx="14" cy="14" r={R} fill="none"
                    stroke={stroke} strokeWidth="3"
                    strokeDasharray={C}
                    strokeDashoffset={C * (1 - pct)}
                    strokeLinecap="round"
                    transform="rotate(-90 14 14)"
                    style={{ transition: "stroke-dashoffset 0.2s" }}
                />
            </svg>
            {pct >= 0.85 && (
                <span className={`font-heading font-bold text-[11px] ${pct >= 1 ? "text-vibrantpink" : "text-ink/60"}`}>
                    {max - length}
                </span>
            )}
        </div>
    );
}

function Composer({ onPosted, activeTag, currentUsername, avatarVersion }) {
    const [text, setText] = useState("");
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const inputRef = useRef(null);

    useEffect(() => {
        if (activeTag && !text.includes(`#${activeTag}`)) {
            // pre-seed hashtag when filtered
            setText((t) => (t ? `${t} #${activeTag}` : `#${activeTag} `));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTag]);

    const pick = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!f.type.startsWith("image/")) return setError("Only images");
        if (f.size > 10 * 1024 * 1024) return setError("Image exceeds 10MB");
        setFile(f);
        setPreview(URL.createObjectURL(f));
        setError("");
    };

    const clearImage = () => {
        setFile(null);
        setPreview("");
        if (inputRef.current) inputRef.current.value = "";
    };

    const submit = async () => {
        const t = text.trim();
        if (!t && !file) return setError("Add text or an image");
        setSubmitting(true);
        setError("");
        try {
            const fd = new FormData();
            fd.append("text", t);
            if (file) fd.append("file", file);
            const { data } = await api.post("/kyc/posts", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setText("");
            clearImage();
            onPosted && onPosted(data);
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setSubmitting(false);
        }
    };

    const disabled = submitting || (!text.trim() && !file) || text.length > MAX_CHARS;

    return (
        <div data-testid="kyc-composer" className="brutal-card p-4 bg-white">
            <div className="flex gap-3">
                <UserAvatar username={currentUsername} size={44} version={avatarVersion} />
                <div className="flex-1">
                    <textarea
                        data-testid="kyc-composer-text"
                        value={text}
                        onChange={(e) => setText(e.target.value.slice(0, MAX_CHARS + 20))}
                        placeholder="What's happening? Drop a promo, giveaway, or #hashtag…"
                        rows={3}
                        className="w-full bg-transparent border-none p-0 font-body text-base sm:text-lg text-ink placeholder:text-ink/40 focus:outline-none resize-none"
                    />
                    {preview && (
                        <div className="relative mt-2 inline-block">
                            <img src={preview} alt="preview" className="max-h-64 rounded-xl border-[3px] border-ink" />
                            <button
                                type="button"
                                onClick={clearImage}
                                data-testid="kyc-composer-remove-image"
                                className="absolute -top-2 -right-2 w-8 h-8 bg-ink text-white border-[3px] border-ink rounded-full flex items-center justify-center shadow-brutal hover:-translate-y-0.5 transition-all"
                            >
                                <X className="w-4 h-4" strokeWidth={3} />
                            </button>
                        </div>
                    )}
                    {error && (
                        <div data-testid="kyc-composer-error" className="mt-2 flex items-start gap-2 bg-vibrantpink/15 border-[3px] border-vibrantpink text-ink rounded-xl p-2">
                            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-vibrantpink" strokeWidth={2.5} />
                            <span className="font-body font-semibold text-xs">{error}</span>
                        </div>
                    )}
                    <div className="mt-3 flex items-center justify-between gap-3 border-t-[2px] border-ink/10 pt-3">
                        <label className="text-vibrantblue hover:bg-vibrantblue/10 rounded-full p-2 cursor-pointer transition-all" data-testid="kyc-composer-image-label" title="Add image">
                            <ImageIcon className="w-5 h-5" strokeWidth={2.5} />
                            <input ref={inputRef} type="file" accept="image/*" onChange={pick} className="hidden" data-testid="kyc-composer-image-input" />
                        </label>
                        <div className="flex items-center gap-3">
                            <CharProgress length={text.length} max={MAX_CHARS} />
                            <button
                                type="button"
                                onClick={submit}
                                disabled={disabled}
                                data-testid="kyc-composer-post-btn"
                                className="brutal-btn-primary text-sm py-2 px-5 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                {submitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" strokeWidth={2.5} /> : null}
                                Post
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ImageLightbox({ src, onClose }) {
    useEffect(() => {
        const h = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", h);
        return () => window.removeEventListener("keydown", h);
    }, [onClose]);
    return (
        <div
            data-testid="kyc-lightbox"
            onClick={onClose}
            className="fixed inset-0 z-50 bg-ink/90 backdrop-blur-sm flex items-center justify-center p-4"
        >
            <button
                type="button"
                onClick={onClose}
                data-testid="kyc-lightbox-close"
                className="absolute top-4 right-4 w-11 h-11 bg-white text-ink border-[3px] border-ink rounded-full flex items-center justify-center shadow-brutal-lg hover:-translate-y-0.5 transition-all"
            >
                <X className="w-5 h-5" strokeWidth={3} />
            </button>
            <img src={src} alt="full" className="max-w-full max-h-full rounded-2xl border-[3px] border-white" onClick={(e) => e.stopPropagation()} />
        </div>
    );
}

function CommentBlock({ postId, isLoggedIn, onCount }) {
    const [comments, setComments] = useState([]);
    const [text, setText] = useState("");
    const [loading, setLoading] = useState(true);
    const [posting, setPosting] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/kyc/posts/${postId}/comments`);
            setComments(data);
        } catch {}
        finally { setLoading(false); }
    }, [postId]);

    useEffect(() => { load(); }, [load]);

    const submit = async () => {
        const t = text.trim();
        if (!t) return;
        setPosting(true);
        try {
            const { data } = await api.post(`/kyc/posts/${postId}/comments`, { text: t });
            setComments((c) => [data, ...c]);
            setText("");
            onCount && onCount(1);
        } catch {}
        finally { setPosting(false); }
    };

    return (
        <div data-testid={`kyc-comments-${postId}`} className="mt-3 border-t-[2px] border-ink/10 pt-3 space-y-3">
            {isLoggedIn && (
                <div className="flex gap-2">
                    <input
                        type="text"
                        value={text}
                        onChange={(e) => setText(e.target.value.slice(0, 500))}
                        onKeyDown={(e) => e.key === "Enter" && submit()}
                        placeholder="Post your reply"
                        data-testid={`kyc-comment-input-${postId}`}
                        className="flex-1 bg-cream border-[3px] border-ink rounded-xl px-3 py-2 font-body text-sm text-ink focus:outline-none"
                    />
                    <button
                        type="button"
                        onClick={submit}
                        disabled={posting || !text.trim()}
                        data-testid={`kyc-comment-submit-${postId}`}
                        className="brutal-btn-primary text-xs py-2 px-3 disabled:opacity-50"
                    >
                        {posting ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5} /> : <Send className="w-4 h-4" strokeWidth={2.5} />}
                    </button>
                </div>
            )}
            {loading ? (
                <div className="font-body text-xs text-ink/50">Loading replies…</div>
            ) : comments.length === 0 ? (
                <div className="font-body text-xs text-ink/50">No replies yet.</div>
            ) : (
                comments.map((c) => (
                    <div key={c.id} data-testid={`kyc-comment-${c.id}`} className="flex gap-2 items-start">
                        <Link to={`/u/${c.username}`} onClick={(e) => e.stopPropagation()} className="flex-shrink-0">
                            <UserAvatar username={c.username} size={28} className="!border-2 !shadow-none" />
                        </Link>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-1.5">
                                <Link to={`/u/${c.username}`} onClick={(e) => e.stopPropagation()} className="font-heading font-bold text-xs text-ink hover:text-vibrantorange">
                                    @{c.username}
                                </Link>
                                <span className="font-body text-[10px] text-ink/40">{timeAgo(c.created_at)}</span>
                            </div>
                            <p className="font-body text-sm text-ink break-words">{c.text}</p>
                        </div>
                    </div>
                ))
            )}
        </div>
    );
}

function ActionBtn({ active, activeColor = "text-vibrantpink", hoverBg = "hover:bg-vibrantpink/10", icon: Icon, count, onClick, testId, title, filled }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            title={title}
            className={`group flex items-center gap-1.5 font-heading font-bold text-xs transition-all ${active ? activeColor : "text-ink/50 hover:" + activeColor.replace("text-", "text-")}`}
        >
            <span className={`w-8 h-8 rounded-full flex items-center justify-center ${hoverBg} transition-all`}>
                <Icon className="w-4 h-4" strokeWidth={2.5} fill={filled && active ? "currentColor" : "none"} />
            </span>
            {count != null && <span data-testid={`${testId}-count`}>{formatCount(count)}</span>}
        </button>
    );
}

function PostCard({ post, onChange, onDelete, isLoggedIn, currentUsername, onTagClick, onOpenImage }) {
    const [showComments, setShowComments] = useState(false);
    const [imgUrl, setImgUrl] = useState("");
    const viewedRef = useRef(false);

    useEffect(() => {
        let revoke = null;
        if (post.has_image) {
            api.get(`/kyc/posts/${post.id}/image`, { responseType: "blob" })
                .then((r) => {
                    const url = URL.createObjectURL(r.data);
                    setImgUrl(url);
                    revoke = url;
                })
                .catch(() => {});
        }
        return () => { if (revoke) URL.revokeObjectURL(revoke); };
    }, [post.id, post.has_image]);

    // Fire a single view when the card mounts
    useEffect(() => {
        if (viewedRef.current) return;
        viewedRef.current = true;
        api.post(`/kyc/posts/${post.id}/view`).catch(() => {});
    }, [post.id]);

    const toggleLike = async (e) => {
        e.stopPropagation();
        if (!isLoggedIn) return;
        try {
            const { data } = await api.post(`/kyc/posts/${post.id}/like`);
            onChange({ ...post, liked_by_me: data.liked, likes_count: data.likes_count });
        } catch {}
    };

    const toggleRepost = async (e) => {
        e.stopPropagation();
        if (!isLoggedIn) return;
        try {
            const { data } = await api.post(`/kyc/posts/${post.id}/repost`);
            onChange({ ...post, reposted_by_me: data.reposted, reposts_count: data.reposts_count });
        } catch {}
    };

    const toggleBookmark = async (e) => {
        e.stopPropagation();
        if (!isLoggedIn) return;
        try {
            const { data } = await api.post(`/kyc/posts/${post.id}/bookmark`);
            onChange({ ...post, bookmarked_by_me: data.bookmarked });
        } catch {}
    };

    const share = async (e) => {
        e.stopPropagation();
        const url = `${window.location.origin}/kyc?post=${post.id}`;
        try {
            if (navigator.share) {
                await navigator.share({ title: `@${post.owner_username} on Endfb`, url });
            } else {
                await navigator.clipboard.writeText(url);
            }
        } catch {}
    };

    const del = async (e) => {
        e.stopPropagation();
        if (!window.confirm("Delete this post?")) return;
        try {
            await api.delete(`/kyc/posts/${post.id}`);
            onDelete(post.id);
        } catch {}
    };

    const isOwner = currentUsername && currentUsername === post.owner_username;

    return (
        <div data-testid={`kyc-post-${post.id}`} className="brutal-card p-4 bg-white hover:bg-cream/40 transition-colors">
            <div className="flex items-start gap-3">
                <Link to={`/u/${post.owner_username}`} onClick={(e) => e.stopPropagation()} className="hover:-translate-y-0.5 transition-all flex-shrink-0">
                    <UserAvatar username={post.owner_username} size={44} />
                </Link>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <Link to={`/u/${post.owner_username}`} onClick={(e) => e.stopPropagation()} data-testid={`kyc-post-author-${post.id}`} className="font-heading font-bold text-sm text-ink hover:underline">
                            {post.owner_username}
                        </Link>
                        <Verified className="w-4 h-4 text-vibrantblue" strokeWidth={2.5} fill="currentColor" />
                        <span className="font-body text-sm text-ink/50">@{post.owner_username}</span>
                        <span className="text-ink/30">·</span>
                        <span className="font-body text-xs text-ink/50">{timeAgo(post.created_at)}</span>
                        {isOwner && (
                            <button
                                type="button"
                                onClick={del}
                                data-testid={`kyc-post-delete-${post.id}`}
                                className="ml-auto text-ink/40 hover:text-vibrantpink"
                                title="Delete"
                            >
                                <Trash2 className="w-4 h-4" strokeWidth={2.5} />
                            </button>
                        )}
                    </div>
                    {post.text && (
                        <p data-testid={`kyc-post-text-${post.id}`} className="mt-1 font-body text-ink whitespace-pre-wrap break-words leading-relaxed">
                            {renderRichText(post.text, onTagClick)}
                        </p>
                    )}
                    {post.has_image && imgUrl && (
                        <div className="mt-3 border-[3px] border-ink rounded-2xl overflow-hidden bg-cream cursor-zoom-in" onClick={(e) => { e.stopPropagation(); onOpenImage(imgUrl); }}>
                            <img src={imgUrl} alt="post" data-testid={`kyc-post-image-${post.id}`} className="w-full max-h-[500px] object-cover hover:opacity-95 transition-opacity" />
                        </div>
                    )}
                    <div className="mt-3 flex items-center justify-between max-w-md">
                        <ActionBtn
                            testId={`kyc-post-comment-${post.id}`}
                            icon={MessageCircle}
                            count={post.comments_count}
                            active={showComments}
                            activeColor="text-vibrantblue"
                            hoverBg="hover:bg-vibrantblue/10"
                            onClick={(e) => { e.stopPropagation(); setShowComments((s) => !s); }}
                            title="Reply"
                        />
                        <ActionBtn
                            testId={`kyc-post-repost-${post.id}`}
                            icon={Repeat2}
                            count={post.reposts_count}
                            active={post.reposted_by_me}
                            activeColor="text-vibrantgreen"
                            hoverBg="hover:bg-vibrantgreen/10"
                            onClick={toggleRepost}
                            title="Repost"
                        />
                        <ActionBtn
                            testId={`kyc-post-like-${post.id}`}
                            icon={Heart}
                            count={post.likes_count}
                            active={post.liked_by_me}
                            activeColor="text-vibrantpink"
                            hoverBg="hover:bg-vibrantpink/10"
                            onClick={toggleLike}
                            title="Like"
                            filled
                        />
                        <ActionBtn
                            testId={`kyc-post-views-${post.id}`}
                            icon={BarChart2}
                            count={post.views_count}
                            active={false}
                            activeColor="text-vibrantblue"
                            hoverBg="hover:bg-vibrantblue/10"
                            onClick={(e) => e.stopPropagation()}
                            title="Views"
                        />
                        <ActionBtn
                            testId={`kyc-post-bookmark-${post.id}`}
                            icon={Bookmark}
                            active={post.bookmarked_by_me}
                            activeColor="text-vibrantblue"
                            hoverBg="hover:bg-vibrantblue/10"
                            onClick={toggleBookmark}
                            title="Bookmark"
                            filled
                        />
                        <ActionBtn
                            testId={`kyc-post-share-${post.id}`}
                            icon={Share2}
                            active={false}
                            activeColor="text-vibrantorange"
                            hoverBg="hover:bg-vibrantorange/10"
                            onClick={share}
                            title="Share"
                        />
                    </div>
                    {showComments && <CommentBlock postId={post.id} isLoggedIn={isLoggedIn} onCount={(d) => onChange({ ...post, comments_count: post.comments_count + d })} />}
                </div>
            </div>
        </div>
    );
}

function TrendingRail({ activeTag, onTagClick }) {
    const [tags, setTags] = useState([]);
    useEffect(() => {
        api.get("/kyc/trending?limit=8").then((r) => setTags(r.data)).catch(() => {});
    }, []);
    return (
        <aside className="hidden lg:block sticky top-24 space-y-4">
            <div className="brutal-card p-4 bg-white">
                <div className="flex items-center gap-2 mb-3">
                    <Flame className="w-5 h-5 text-vibrantorange" strokeWidth={2.5} />
                    <h3 className="font-heading font-black text-lg tracking-tighter text-ink">Trending</h3>
                </div>
                {tags.length === 0 ? (
                    <p className="font-body text-xs text-ink/50">No trends yet — start a #hashtag!</p>
                ) : (
                    <div className="space-y-1">
                        {tags.map((t) => (
                            <button
                                key={t.tag}
                                type="button"
                                onClick={() => onTagClick(t.tag)}
                                data-testid={`kyc-trend-${t.tag}`}
                                className={`w-full text-left px-3 py-2 rounded-xl transition-all ${activeTag === t.tag ? "bg-vibrantyellow" : "hover:bg-cream"}`}
                            >
                                <div className="flex items-center gap-1.5">
                                    <Hash className="w-3.5 h-3.5 text-ink/40" strokeWidth={2.5} />
                                    <span className="font-heading font-bold text-sm text-ink">{t.tag}</span>
                                </div>
                                <div className="font-body text-[11px] text-ink/50 ml-5">{t.count} post{t.count === 1 ? "" : "s"}</div>
                            </button>
                        ))}
                    </div>
                )}
            </div>
            <div className="brutal-card p-4 bg-vibrantyellow">
                <h3 className="font-heading font-black text-lg tracking-tighter text-ink mb-1">What is KYC?</h3>
                <p className="font-body text-xs text-ink/70">
                    Post promotions, shout out giveaways, and rally the community. Like, repost and reply — just like your other favorite bird site.
                </p>
            </div>
        </aside>
    );
}

export default function Kyc() {
    const { user } = useAuth();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sort, setSort] = useState("new");
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTag = searchParams.get("tag") || "";
    const [lightbox, setLightbox] = useState("");

    const isLoggedIn = user && user !== false;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ sort, limit: "100" });
            if (activeTag) params.set("tag", activeTag);
            const { data } = await api.get(`/kyc/posts?${params.toString()}`);
            setPosts(data);
        } catch {}
        finally { setLoading(false); }
    }, [sort, activeTag]);

    useEffect(() => { load(); }, [load]);

    const onPosted = (p) => setPosts((prev) => [p, ...prev]);
    const onChange = (p) => setPosts((prev) => prev.map((x) => (x.id === p.id ? p : x)));
    const onDelete = (id) => setPosts((prev) => prev.filter((x) => x.id !== id));

    const setTag = (tag) => {
        const next = new URLSearchParams(searchParams);
        if (activeTag === tag) next.delete("tag");
        else next.set("tag", tag);
        setSearchParams(next, { replace: true });
    };

    const clearTag = () => {
        const next = new URLSearchParams(searchParams);
        next.delete("tag");
        setSearchParams(next, { replace: true });
    };

    return (
        <>
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-6">
                <div className="min-w-0">
                    <div className="sticky top-16 md:top-20 z-20 -mx-4 sm:mx-0 px-4 sm:px-0 py-3 bg-cream/95 backdrop-blur border-b-[3px] border-ink/10 mb-4">
                        <div className="flex items-center gap-2 mb-2">
                            <div className="w-10 h-10 bg-vibrantpink border-[3px] border-ink rounded-xl flex items-center justify-center shadow-brutal flex-shrink-0">
                                <Sparkles className="w-5 h-5 text-white" strokeWidth={2.5} />
                            </div>
                            <div>
                                <h1 className="font-heading font-black text-2xl sm:text-3xl tracking-tighter text-ink leading-none">
                                    KYC <span className="text-vibrantpink">Promotions</span>
                                </h1>
                                <p className="font-body text-xs text-ink/60">Giveaways · Shout-outs · Real talk</p>
                            </div>
                        </div>
                        <div className="flex gap-2 items-center flex-wrap" data-testid="kyc-sort-tabs">
                            <button
                                type="button"
                                onClick={() => setSort("new")}
                                data-testid="kyc-sort-new"
                                className={`flex-1 sm:flex-none brutal-btn py-1.5 px-3 text-xs ${sort === "new" ? "bg-ink text-white" : "bg-white text-ink"}`}
                            >
                                <Clock className="w-3.5 h-3.5 mr-1.5" strokeWidth={2.5} /> Newest
                            </button>
                            <button
                                type="button"
                                onClick={() => setSort("top")}
                                data-testid="kyc-sort-top"
                                className={`flex-1 sm:flex-none brutal-btn py-1.5 px-3 text-xs ${sort === "top" ? "bg-vibrantpink text-white" : "bg-white text-ink"}`}
                            >
                                <Flame className="w-3.5 h-3.5 mr-1.5" strokeWidth={2.5} /> Most-liked
                            </button>
                            {activeTag && (
                                <button
                                    type="button"
                                    onClick={clearTag}
                                    data-testid="kyc-clear-tag"
                                    className="brutal-btn py-1.5 px-3 text-xs bg-vibrantyellow text-ink"
                                >
                                    <Hash className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} />
                                    {activeTag}
                                    <X className="w-3.5 h-3.5 ml-1" strokeWidth={2.5} />
                                </button>
                            )}
                        </div>
                    </div>

                    {isLoggedIn ? (
                        <div className="mb-4">
                            <Composer onPosted={onPosted} activeTag={activeTag} currentUsername={user?.username} avatarVersion={user?.has_avatar ? "1" : "0"} />
                        </div>
                    ) : (
                        <div data-testid="kyc-login-gate" className="brutal-card p-4 bg-vibrantyellow mb-4">
                            <p className="font-heading font-bold text-sm text-ink">
                                <Link to="/login" className="text-vibrantorange hover:underline">Log in</Link> to post, like, repost, and reply.
                            </p>
                        </div>
                    )}

                    {loading ? (
                        <div className="font-heading font-bold text-ink/60 text-center py-12">
                            <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" strokeWidth={2.5} />
                            Loading feed…
                        </div>
                    ) : posts.length === 0 ? (
                        <div data-testid="kyc-empty" className="brutal-card p-8 bg-cream text-center">
                            <Sparkles className="w-10 h-10 mx-auto text-ink/40 mb-2" strokeWidth={2} />
                            <p className="font-heading font-bold text-ink/60">
                                {activeTag ? `No posts tagged #${activeTag} yet.` : "No posts yet — be the first!"}
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-3" data-testid="kyc-feed">
                            {posts.map((p) => (
                                <PostCard
                                    key={p.id}
                                    post={p}
                                    onChange={onChange}
                                    onDelete={onDelete}
                                    isLoggedIn={isLoggedIn}
                                    currentUsername={user?.username}
                                    onTagClick={setTag}
                                    onOpenImage={setLightbox}
                                />
                            ))}
                        </div>
                    )}
                </div>

                <TrendingRail activeTag={activeTag} onTagClick={setTag} />
            </div>
            {lightbox && <ImageLightbox src={lightbox} onClose={() => setLightbox("")} />}
        </>
    );
}
